var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { StepLayout } from '@/components/step-layout.component';
import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useResultCategoryOutletContext } from '@/components/results-category/hooks/use-result-category-outlet-context.hook';
import { useNavigator } from 'hx-navigator';
import { useDispatch, useSelector } from 'react-redux';
import { dinTestActions } from '@/store';
import { isResultsCategory } from '@/components/results-category/helpers/is-results-category.helper';
import { ErrorMessage } from '@/components/error-message.component';
import { NavigationButton } from '@/components/navigation-button.component';
import { useTestCompletionCallback } from '@/hooks/use-test-completion-callback.hook';
import { createWizardStore } from 'hx-wizard';
import { createWidgetStore } from 'hx-widget-store';
import { buildResultCsvFileName, createResultCsv, } from '@/helpers/result-csv.helper';
export default function ResultCategoryPage() {
    var _a = useState(''), setImage = _a[1];
    var _b = useState(0), setStep = _b[1];
    var navigate = useNavigator().navigate;
    var location = useLocation();
    var handleTestCompletion = useTestCompletionCallback();
    var dispatch = useDispatch();
    var wizardActions = createWizardStore().wizardActions;
    var widgetActions = createWidgetStore().widgetActions;
    var category = useSelector(function (state) { return state.dinTest.result.category; });
    var dinTestResult = useSelector(function (state) { return state.dinTest.result; });
    var dinTestSteps = useSelector(function (state) { return state.dinTest.steps; });
    var wizardForm = useSelector(function (state) { return state.wizard.form; });
    var previewCategory = new URLSearchParams(location.search).get('previewCategory');
    var previewCategoryValue = isResultsCategory(previewCategory || undefined)
        ? previewCategory
        : undefined;
    var isPreviewMode = process.env.NODE_ENV !== 'production' && !!previewCategoryValue;
    useEffect(function () {
        if (!isPreviewMode)
            return;
        if (category === previewCategoryValue)
            return;
        dispatch(dinTestActions.update({
            completion: 'complete',
            result: {
                category: previewCategoryValue,
                timestamp: Date.now() / 1000,
            },
        }));
    }, [category, dispatch, isPreviewMode, previewCategoryValue]);
    useEffect(function () {
        if (!category)
            return;
        var resultPath = "/result-category/".concat(category);
        if (location.pathname === resultPath)
            return;
        if (!isPreviewMode) {
            handleTestCompletion();
        }
        navigate("".concat(resultPath).concat(isPreviewMode ? location.search : ''));
    }, [
        category,
        handleTestCompletion,
        isPreviewMode,
        location.pathname,
        location.search,
        navigate,
    ]);
    if (!category || !isResultsCategory(category)) {
        return (_jsx(ErrorMessage, { action: function () { return navigate('/'); }, actionText: "Repasser le test", message: "Oups! Impossible d'afficher les r\u00E9sultats" }));
    }
    function handleEndSessionClick() {
        dispatch(dinTestActions.reset());
        dispatch(wizardActions.reset());
        dispatch(widgetActions.startNewSession());
        navigate('/');
    }
    function handleRetestParticipantClick() {
        dispatch(dinTestActions.reset());
        navigate('/din-test');
    }
    function handleDownloadCsvClick() {
        var referenceNumber = toStringValue(wizardForm['reference-number']);
        var birthYear = wizardForm['birth-year'];
        var volumeLevel = wizardForm.volume;
        var hearingStatus = wizardForm['hearing-status'];
        var headphones = true;
        var csv = createResultCsv({
            referenceNumber: referenceNumber,
            birthYear: isStringOrNumber(birthYear) ? birthYear : undefined,
            language: 'fr',
            volumeLevel: isStringOrNumber(volumeLevel) ? volumeLevel : undefined,
            headphones: headphones,
            hearingStatus: isStringOrNumber(hearingStatus)
                ? hearingStatus
                : undefined,
            result: dinTestResult,
            steps: dinTestSteps,
        });
        var fileName = buildResultCsvFileName(referenceNumber, dinTestResult.timestamp);
        var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        var url = window.URL.createObjectURL(blob);
        var link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(url);
    }
    return (_jsx(StepLayout, { showProgress: false, image: "", heading: _jsxs(_Fragment, { children: ["R\u00E9sultats ", _jsx("span", __assign({ className: "font-normal text-primary" }, { children: "RSB" }))] }), content: _jsx(Outlet, { context: { setImage: setImage, setStep: setStep } }), nav: _jsxs("div", __assign({ className: "flex flex-col lg:flex-row gap-4" }, { children: [_jsx(NavigationButton, __assign({ variant: "outlined", onClick: handleRetestParticipantClick, className: "py-2 text-xl whitespace-nowrap w-auto sm:w-auto px-0" }, { children: "Retester le participant" })), _jsx(NavigationButton, __assign({ variant: "outlined", onClick: handleEndSessionClick, className: "py-2 text-xl whitespace-nowrap w-auto sm:w-auto px-8" }, { children: "Tester un nouveau participant" })), _jsx(NavigationButton, __assign({ variant: "outlined", onClick: handleDownloadCsvClick, className: "py-2 text-xl whitespace-nowrap w-auto sm:w-auto px-8" }, { children: "T\u00E9l\u00E9charger les r\u00E9sultats (CSV)" }))] })) }));
}
export function ResultCategoryOutlet() {
    return _jsx(Outlet, { context: useResultCategoryOutletContext() });
}
function toStringValue(value) {
    return typeof value === 'string' ? value : undefined;
}
function isStringOrNumber(value) {
    return typeof value === 'string' || typeof value === 'number';
}
